import type { IAddress } from '@curbnturf/entities';
import type { Amenity, RvType, CancellationTier, IAddOn, Category } from './mock-listings.data';

/** R15/A — Per-photo edit metadata. Backend POST /site/:id/upload-image
 *  accepts these exact fields; on display it returns a 1:1 crop rendered
 *  from the stored source using these values. Missing photoMeta => no crop
 *  and no rotation (i.e., the full 1200px frame renders). */
export interface IPhotoMeta {
  /** Normalized 0..1 relative to the (post-rotation) image bounds. */
  crop: { left: number; top: number; right: number; bottom: number };
  /** Degrees clockwise. Only right-angles for the "limited features" scope. */
  rotation: 0 | 90 | 180 | 270;
}

// ─────────────────────── Amenity grouping + presets ───────────────────────

/** Five visual groupings for the wizard amenities step. */
export type AmenityGroup = 'site-access' | 'power-water' | 'connectivity' | 'comfort' | 'outdoor';

export const AMENITY_GROUP_LABELS: Record<AmenityGroup, string> = {
  'site-access': 'Site access',
  'power-water': 'Power & water',
  'connectivity': 'Connectivity',
  'comfort':      'Comfort',
  'outdoor':      'Outdoor life',
};

export const AMENITY_GROUPS_ORDER: AmenityGroup[] = [
  'site-access', 'power-water', 'connectivity', 'comfort', 'outdoor',
];

/** Which group each amenity belongs to — drives the wizard step layout. */
export const AMENITY_GROUP_FOR: Record<Amenity, AmenityGroup> = {
  // Site access
  'backIn':           'site-access',
  'pullThrough':      'site-access',
  'vehicles':  'site-access',
  'tents':     'site-access',
  'wheelchair':        'site-access',
  'lockableStorage':  'site-access',
  // Power & water
  'electricity':       'power-water',
  'water':     'power-water',
  'sewage':            'power-water',
  'dump':      'power-water',
  'waterFill':        'power-water',
  'evCharging':       'power-water',
  'generatorOk':      'power-water',
  // Connectivity
  'cell':     'connectivity',
  'wifi':              'connectivity',
  // Comfort
  'table':      'comfort',
  'restroom':            'comfort',
  'shower':            'comfort',
  'laundry':           'comfort',
  'pool':           'comfort',
  'outdoorLighting':  'comfort',
  'sunshade':          'comfort',
  // Outdoor life
  'firesAllowed':         'outdoor',
  'fireRing':         'outdoor',
  'outdoorGrill':     'outdoor',
  'petsAllowed':              'outdoor',
  'trash':             'outdoor',
  'recycling':           'outdoor',
};

/** Quick-pick presets — additive bundles of amenities for fast setup. */
export interface IAmenityPreset {
  key: string;
  label: string;
  description: string;
  icon: string;
  amenities: Amenity[];
}

export const AMENITY_PRESETS: IAmenityPreset[] = [
  {
    key: 'full-hookups-rv',
    label: 'Full hookups RV park',
    description: 'Electric, water, sewage, wifi, trash',
    icon: 'rv_hookup',
    amenities: ['electricity', 'water', 'sewage', 'dump', 'wifi', 'trash', 'vehicles', 'generatorOk'],
  },
  {
    key: 'backcountry-minimal',
    label: 'Backcountry minimal',
    description: 'Fire, trash, recycle — pack-it-in vibes',
    icon: 'park',
    amenities: ['firesAllowed', 'fireRing', 'trash', 'recycling'],
  },
  {
    key: 'standard-farm-stay',
    label: 'Standard farm stay',
    description: 'Water, toilet, pets, picnic table',
    icon: 'agriculture',
    amenities: ['water', 'restroom', 'petsAllowed', 'table', 'trash'],
  },
];

/** Max custom amenities a host can add — keeps the chip list scannable. */
export const MAX_CUSTOM_AMENITIES = 8;

/** Power amperages the host can claim when electricity is selected. */
export type ElectricityAmps = '15A' | '30A' | '50A';

/** How guests get potable water when water is selected — a hookup at the site, or a
 *  shared fill station on the property. Stored verbatim as the water `IAmenity.options`. */
export type WaterHookup = 'direct' | 'station';

/** Wifi speed buckets the host can claim when wifi is selected. */
export type WifiSpeed = 'slow' | 'decent' | 'fast';

/** Pet policy when pets are selected. */
export type PetsPolicy = 'free' | 'fee' | 'size-limit';

/**
 * Tent allowance on the Vehicles step.
 *   'allowed'    — tents OK alongside the host's accepted RV types.
 *   'tents-only' — tents only, no RVs accepted.
 *   undefined    — tents not accepted (default).
 */
export type TentMode = 'allowed' | 'tents-only';

/**
 * Primary property type — single-select on the first wizard step. Drives the
 * listing-descriptor phrasing ("Brewery stay in Minneapolis, MN") and maps
 * into the platform-wide `Category` bucket for the visual pennant + amenities.
 */
export type PrimaryPropertyType =
  | 'winery' | 'brewery' | 'distillery'
  | 'farm' | 'specialty-farm' | 'orchard' | 'ranch'
  | 'golf-course' | 'church' | 'rv-park'
  | 'woodlands' | 'private-home' | 'custom';

/** Max custom secondary descriptors the host can add — keeps the chip row from sprawling. */
export const MAX_CUSTOM_DESCRIPTORS = 5;

/** Visual grouping for the wizard's primary-type tiles. */
export type PrimaryPropertyGroup = 'hospitality' | 'land' | 'other';

export const PRIMARY_GROUP_LABELS: Record<PrimaryPropertyGroup, string> = {
  hospitality: 'Hospitality',
  land:        'Land-based business',
  other:       'Other venues',
};

/**
 * Secondary descriptors — multi-select setting/landscape qualifiers that
 * further describe the property. Optional; the primary type alone is enough
 * to publish.
 */
export type PropertyDescriptor =
  | 'backyard' | 'driveway' | 'curbside'
  | 'beach' | 'coastal' | 'lake' | 'river-stream'
  | 'mountains' | 'desert' | 'canyon' | 'forest'
  | 'hot-spring' | 'waterfall' | 'wetlands' | 'plain-prairie'
  | 'urban' | 'suburban';

/** What guests can see from the site. Multi-select. */
export type VisibilityKind = 'secluded' | 'home-visible' | 'neighbors' | 'other-guests';

/** Noise level — single-select radio. */
export type NoiseLevel = 'silence' | 'sounds-of-nature' | 'periodic' | 'constant';

/** Road conditions to reach the site. Multi-select. */
export type RoadCondition = 'paved' | 'smooth-gravel' | 'poor-gravel' | 'high-clearance' | 'off-road';

/** Check-in process. */
export type CheckInProcess = 'meet-greet' | 'self-checkin' | 'lockbox';

/** Whether bookings are auto-confirmed or require host approval. */
export type Bookability = 'instant' | 'request';

/** Whether the property sits on private or public land. */
export type LandType = 'private' | 'public';

/**
 * Address-step (Phase 1, step 1) validity. Requires city + state + a map pin,
 * plus — when the host is NOT the landowner — the land type, the landowner's
 * name + phone, and the represents-landowner answer. Shared by the wizard
 * service and the phase hub so the two never drift.
 */
export function isAddressStepValid(d: IDraftListing): boolean {
  const baseOk = !!d.address?.city && !!d.address?.state && typeof d.lat === 'number';
  if (!baseOk) return false;
  if (d.isLandowner === false) {
    return !!d.landType
      && !!d.landowner?.firstName?.trim()
      && !!d.landowner?.lastName?.trim()
      && !!d.landowner?.phone?.trim()
      && d.representsLandowner !== undefined;
  }
  return true;
}

/** Landowner contact captured when the host is not the owner — verification only, never shown to guests. */
export interface ILandownerContact {
  firstName: string;
  lastName: string;
  phone: string;
  address: IAddress;
}

/** House-rule toggles. */
export interface IHouseRules {
  noSmoking: boolean;
  noParties: boolean;
  quietHours: boolean;
  noFireworks: boolean;
  noFirearms: boolean;
}

/**
 * Draft state for the host onboarding wizard. Persisted to `cnt-listing-draft`
 * in localStorage; transformed to `IPrivateListing` on publish.
 *
 * All step fields are optional so a host can save & exit at any point. The
 * service validates required fields at publish time.
 */
export interface IDraftListing {
  id: string;                          // draft UUID
  createdAt: string;                   // ISO
  updatedAt: string;                   // ISO
  /** Which phase the host is currently on (1, 2, or 3). */
  currentPhase: 1 | 2 | 3;
  /** Step index within the current phase (0-based). */
  currentStep: number;

  // ─────────────── Phase 1 — Tell us about your place ───────────────
  /** Single-select business type — required. Drives category + descriptor phrase. */
  primaryType?: PrimaryPropertyType;
  /** Required when `primaryType === 'custom'` — the host's own label. */
  customPrimaryLabel?: string;
  /** Multi-select setting descriptors — optional. */
  descriptors?: PropertyDescriptor[];
  /** Free-text secondary descriptors the host added — optional. */
  customDescriptors?: string[];
  address?: IAddress;
  lat?: number;
  lng?: number;
  isLandowner?: boolean;
  /** Set only when `isLandowner === false`. */
  landType?: LandType;
  landowner?: ILandownerContact;
  representsLandowner?: boolean;
  maxRig?: { length: number; width: number; height: number };
  guestCapacity?: number;
  amenities?: Amenity[];
  /** Free-text amenities the host added (e.g., "Mini fridge"). Max 8. */
  customAmenities?: string[];
  /** Sub-detail: which amperages are available when 'electricity' is selected. */
  electricityAmps?: ElectricityAmps[];
  /** Sub-detail: hookup vs. shared fill station when 'water' is selected. */
  waterHookup?: WaterHookup;
  /** Sub-detail: rough wifi speed bucket when 'wifi' is selected. */
  wifiSpeed?: WifiSpeed;
  /** Sub-detail: pets policy when 'petsAllowed' is selected. */
  petsPolicy?: PetsPolicy;
  /** Sub-detail: whether the shower has hot water when 'shower' is selected. */
  showerHotWater?: boolean;
  vehicleTypes?: RvType[];
  /** Whether tents are allowed (and whether RVs are excluded). */
  tentMode?: TentMode;

  // ─────────────── Phase 2 — Make it stand out ───────────────
  /** Downscaled JPEG data URLs (~1200px max dimension, ~85% quality). */
  photos?: string[];
  /** Optional per-photo captions, index-aligned with `photos`. */
  photoCaptions?: string[];
  /** Optional per-photo crop rect + rotation, index-aligned with `photos`.
   *  Missing entries mean "no crop, no rotation" (backend serves full 1200px
   *  frame). Backend endpoint POST /listing/:id/upload-image already accepts
   *  cropLeft/Top/Right/Bottom + rotation; syncDraftPhotos forwards these
   *  values so the server can serve the 1:1 crop for display. */
  photoMeta?: IPhotoMeta[];
  title?: string;
  description?: string;
  visibility?: VisibilityKind[];
  noiseLevel?: NoiseLevel;
  roadConditions?: RoadCondition[];
  hasHazards?: boolean;
  hazardsNote?: string;
  profilePhoto?: string;
  /** Host name/phone typed on the profile step while signed out. A signed-in
   *  host's values go straight to the account instead; these fill whichever
   *  account fields are still blank at publish (`HostListingDraftService.publish()`). */
  hostProfile?: { firstName?: string; lastName?: string; phone?: string };

  // ─────────────── Phase 3 — Finish up & publish ───────────────
  checkInTime?: string;
  checkOutTime?: string;
  minNights?: number;
  maxNights?: number;
  minNoticeHours?: number;
  maxNoticeWeeks?: number;
  checkInProcess?: CheckInProcess;
  bookability?: Bookability;
  rules?: IHouseRules;
  customRules?: string;
  cancellationTier?: CancellationTier;
  nightlyPrice?: number;

  // ─────────────── Edit-only (post-publish) ───────────────
  /** Add-ons offered to guests. Editable only via /properties/:id/edit. */
  addOns?: IAddOn[];

  // ─────────────── Status ───────────────
  publishedAt?: string;
  publishedListingId?: number;
  /** Set when this draft was created via Duplicate. Drives the
   *  "Duplicated from …" chip in the wizard top-bar and is cleared on publish. */
  clonedFromListingId?: number;
  /** Set the first time a sales rep hands this in-progress draft off to the
   *  client via "Email client to finish" — the real (unpublished) backend
   *  listing id created for that handoff. Repeat clicks resend the
   *  notification against this same listing instead of creating another. */
  handoffListingId?: number;
  /** The real listing + site created by a publish attempt that the server then
   *  refused to enable. Created but NOT live — which is exactly the distinction
   *  `publishedListingId` cannot carry.
   *
   *  Without these, every rejected publish orphaned another listing: the rows
   *  already existed, nothing rolled them back, and the next click ran the whole
   *  creation sequence again (user-test F14). Recorded before `enable` is asked,
   *  so a rejection leaves something to retry against; the retry reconfigures
   *  these rows from the corrected draft instead of creating more. Same pattern
   *  as `handoffListingId` above. Cleared on a successful publish. */
  pendingListingId?: number;
  pendingSiteId?: number;

  // ─────────────── Sales (Grow) — building a listing on a client's behalf ───────────────
  /** Set when a sales rep is building this listing for a referred client — the
   *  real backend listing is created under this user id, not the signed-in rep's.
   *  Drives the review step's "Create listing for …" CTA and hand-off flow. */
  salesForUserId?: number;
  /** Display name only, for the wizard/review-step copy and the published snapshot's host name. */
  salesForUserName?: string;
  /** Used for the published snapshot's host email — not sent to the backend. */
  salesForUserEmail?: string;

  /** Which persona-landing funnel referred this draft (?source=…). First-touch. */
  sourcePersona?: SourcePersona;
}

export type SourcePersona =
  | 'hh'
  | 'farm'
  | 'land'
  | 'driveway'
  | 'winery'
  | 'brewery'
  | 'attraction';

export const VALID_SOURCE_PERSONAS: ReadonlySet<SourcePersona> = new Set([
  'hh', 'farm', 'land', 'driveway', 'winery', 'brewery', 'attraction',
]);

/**
 * Primary-type metadata. `category` is the 5-bucket platform category the
 * listing belongs to (for the pennant + amenities pool). `descriptorPhrase`
 * leads the listing detail's title line ("Brewery stay in Minneapolis, MN").
 * `description` is a one-line explainer shown beneath the tile label.
 * `group` drives the wizard's sub-section grouping. `sampleListingId` (when
 * present) lets the "Help me pick" modal show a real example.
 */
export const PRIMARY_PROPERTY_TYPE_META: Record<PrimaryPropertyType, {
  label: string;
  icon: string;
  category: Category;
  descriptorPhrase: string;
  description: string;
  group: PrimaryPropertyGroup;
  sampleListingId?: number;
}> = {
  'winery':         { label: 'Winery',         icon: 'wine_bar',       category: 'vineyard',   descriptorPhrase: 'Winery stay',         description: 'Tasting room, vineyard rows, wine production.', group: 'hospitality', sampleListingId: 1 },
  'brewery':        { label: 'Brewery',        icon: 'sports_bar',     category: 'brewery',    descriptorPhrase: 'Brewery stay',        description: 'Taproom, beer garden, on-site brewing.',         group: 'hospitality', sampleListingId: 21 },
  'distillery':     { label: 'Distillery',     icon: 'science',        category: 'brewery',    descriptorPhrase: 'Distillery stay',     description: 'Spirits production with public tasting space.',  group: 'hospitality' },
  'rv-park':        { label: 'RV Park',        icon: 'rv_hookup',      category: 'attraction', descriptorPhrase: 'RV-park stay',        description: 'Established campground with multiple sites.',    group: 'hospitality' },
  'farm':           { label: 'Farm',           icon: 'agriculture',    category: 'farm',       descriptorPhrase: 'Farm stay',           description: 'Working farm — crops, livestock, or mixed-use.', group: 'land',        sampleListingId: 11 },
  'specialty-farm': { label: 'Specialty farm', icon: 'local_florist',  category: 'farm',       descriptorPhrase: 'Specialty-farm stay', description: 'Lavender, dairy, herbs, hemp, hops, flowers.',   group: 'land' },
  'orchard':        { label: 'Orchard',        icon: 'park',           category: 'farm',       descriptorPhrase: 'Orchard stay',        description: 'Apple, peach, citrus — fruit-tree property.',    group: 'land' },
  'ranch':          { label: 'Ranch',          icon: 'pets',                  category: 'farm',       descriptorPhrase: 'Ranch stay',          description: 'Cattle, horses, larger working acreage.',        group: 'land' },
  'woodlands':      { label: 'Woodlands',      icon: 'forest',         category: 'offgrid',    descriptorPhrase: 'Woodlands stay',      description: 'Privately-held forested land.',                  group: 'land' },
  'golf-course':    { label: 'Golf course',    icon: 'golf_course',    category: 'attraction', descriptorPhrase: 'Golf-course stay',    description: 'Course-adjacent or on-property hosting.',        group: 'other' },
  'church':         { label: 'Church',         icon: 'church',         category: 'attraction', descriptorPhrase: 'Church stay',         description: 'Parking lot or grounds of a place of worship.',  group: 'other' },
  'private-home':   { label: 'Private home',   icon: 'home',           category: 'offgrid',    descriptorPhrase: 'Private-home stay',   description: 'Backyard, driveway, or residential land.',       group: 'other', sampleListingId: 51 },
  'custom':         { label: 'Other (custom)',  icon: 'edit_note',      category: 'offgrid',    descriptorPhrase: 'Custom stay',         description: "Doesn't fit the buckets above? Name your own.", group: 'other' },
};

/**
 * Resolve the descriptor phrase for a draft, honoring the host's custom
 * label when `primaryType === 'custom'`. Single source so the wizard's
 * subhead, the phase-3 review fact strip, and (future) listing-detail use
 * the same string.
 */
export function primaryDescriptorPhrase(d: IDraftListing | null | undefined): string {
  if (!d?.primaryType) return 'Brewery stay';
  if (d.primaryType === 'custom') {
    const label = (d.customPrimaryLabel || '').trim();
    return label ? `${label} stay` : 'Custom stay';
  }
  return PRIMARY_PROPERTY_TYPE_META[d.primaryType].descriptorPhrase;
}

/** Display metadata for the secondary descriptors — label + material icon. */
export const PROPERTY_DESCRIPTOR_META: Record<PropertyDescriptor, { label: string; icon: string }> = {
  'backyard':      { label: 'Backyard',       icon: 'yard' },
  'driveway':      { label: 'Driveway',       icon: 'directions_car' },
  'curbside':      { label: 'Curbside',       icon: 'route' },
  'beach':         { label: 'Beach',          icon: 'beach_access' },
  'coastal':       { label: 'Coastal',        icon: 'waves' },
  'lake':          { label: 'Lake',           icon: 'water' },
  'river-stream':  { label: 'River / Stream', icon: 'water_drop' },
  'mountains':     { label: 'Mountains',      icon: 'landscape' },
  'desert':        { label: 'Desert',         icon: 'wb_sunny' },
  'canyon':        { label: 'Canyon',         icon: 'landscape' },
  'forest':        { label: 'Forest',         icon: 'forest' },
  'hot-spring':    { label: 'Hot Spring',     icon: 'hot_tub' },
  'waterfall':     { label: 'Waterfall',      icon: 'water' },
  'wetlands':      { label: 'Wetlands',       icon: 'grass' },
  'plain-prairie': { label: 'Plain / Prairie',icon: 'grass' },
  'urban':         { label: 'Urban',          icon: 'location_city' },
  'suburban':      { label: 'Suburban',       icon: 'home' },
};
